import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Input,
  Label,
  Select,
  Textarea,
  PackageDimensionsForm,
  Tooltip,
} from "@src/common/components";
import CourieButton from "@src/common/components/Button/Button";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import ControllerInput from "./ControllerInput";
import CostCenterSelector from "./CostCenterSelector";
import ServiceTypeSelector from "@src/orders/create/components/ServiceTypeSelector";
import EndCustomerSearchInput from "@src/endcustomers/components/EndCustomerSearchInput";
import { EditOrderShipment } from "@src/shipments/components/EditOrderModalComponent/hooks/useEditOrderShipment";
import {
  CustomFieldType,
  EndCustomer,
  ServiceType,
  ServiceTypeDueTimeType,
  Shipment,
  ShipmentStopType,
  ShipmentType,
  Stop,
  CustomFieldCustomerRequirementType,
  VehicleType,
  useGetVehicleTypesLazyQuery,
} from "@api/graphql/generated/generated-types";
import { getOptionLabelForShipmentType } from "@src/common/lib/textUtils";
import { FormErrorMessage } from "@src/common/components";
import StopForm from "./StopForm";
import AddStopButton from "@src/orders/create/components/AddStopButton";
import { ArrowsUpDownIcon, XCircleIcon } from "@heroicons/react/24/outline";
import {
  isStopsDropdownDisabled,
  returnObjectForSelectValue,
  shipmentTypeArray,
} from "@src/common/lib/EditShipmentSelectOptionsUtils";
import DurationTypeSelector from "@src/orders/create/components/GeneralForm/components/DurationTypeSelector";
import moment from "moment";
import { AuthContext } from "@src/auth/components/AuthProvider";
import "moment-business-days"; // Import moment-business-days to extend moment
import { momentToTimePickerDate } from "@src/common/utils/timezoneConverters";
import { getNumericFieldRangeLabel } from "@src/configureCustomFields/utils/customFieldFormUtils";
import FormInput from "@src/orders/create/components/FormInput";
import {
  isServiceTypeRequired,
  isVehicleTypeRequired,
  isCallerRequired,
  isCallerPhoneRequired,
  isSuiteRequired,
  isPhoneNumberRequired,
  isCompanyNameRequired,
  isPackageWeightRequired,
  isPackageQuantityRequired,
  isPackageDimensionsRequired,
  isPackageDescriptionRequired,
  isPackageSizeRequired,
  isPackageTypeRequired,
} from "@src/common/lib/requiredAdditionalFieldUtils";
import CallerPhoneInput, { CallerPhoneOption } from "@src/common/components/CallerPhoneInput";
import { useServiceTypeAutoFill } from "@src/shipments/hooks/useServiceTypeAutoFill";
import { useUpdateReadyTime } from "@src/shipments/hooks/useUpdateReadyTime";
import { useCustomFields } from "@src/orders/create/hooks/useCustomFields";
import { useVisiblePackageTypes } from "@src/orders/create/hooks/useVisiblePackageTypes";
import { usePackageTypesForOrder, PACKAGE_TYPES_LOAD_ERROR_MESSAGE } from "@src/orders/create/hooks/usePackageTypesForOrder";
import { useApplyPackageTypeDefaults } from "@src/orders/create/hooks/useApplyPackageTypeDefaults";
import { useFilterVehicleTypesByPackageTypes } from "@src/orders/create/hooks/useFilterVehicleTypesByPackageTypes";
import { usePackageDimensionsValidation } from "@src/orders/create/hooks/usePackageDimensionsValidation";
import { OrderCredentialsInputs } from "@src/driverCompliancePolicies/OrderCredentialsInputs";
import { useEditOrderDriverCredentials } from "@src/shipments/hooks/useEditOrderDriverCredentials";
import useEditOrderSwapStops from "@src/shipments/hooks/useEditOrderSwapStops";
import { StopOrderRowControls, getCompletedStopIds, useStopOrderControls } from "@src/shipments/features/stopReordering";
import { packageTypeOrSizeMode } from "@src/common/components/PackageDimensionsForm/packageTypeOrSizeMode";

interface Props {
  stopIdsToDelete: string[];
  setStopIdsToDelete: (stopIdsToDelete: string[]) => void;
  setIsAllStopsHaveAddress: (isAllStopsHaveAddress: boolean) => void;
  selectedDuration: string;
  setSelectedDuration: React.Dispatch<React.SetStateAction<string>>;
  onStopDateChange: () => void;
  shipment: EditOrderShipment;
  setHasPackageValidationError: (hasPackageValidationError: boolean) => void;
  setArePackageTypesLoading: (arePackageTypesLoading: boolean) => void;
  isStopOrderEditable?: boolean;
}

function EditOrderForm({
  stopIdsToDelete,
  setStopIdsToDelete,
  setIsAllStopsHaveAddress,
  selectedDuration,
  setSelectedDuration,
  onStopDateChange,
  shipment,
  setHasPackageValidationError,
  setArePackageTypesLoading,
  isStopOrderEditable = false,
}: Props) {
  const { courier } = useContext(AuthContext);
  const [selectedCustomer, setSelectedCustomer] = useState<EndCustomer | undefined>(undefined);

  const [getVehicleTypes, { data: GetVehicleTypesData }] = useGetVehicleTypesLazyQuery();

  const courierId = courier?.courier?.id;
  const orderId = shipment?.order.id;

  const {
    control,
    setValue,
    formState: { errors, touchedFields },
    watch,
    setError,
    clearErrors,
    getValues,
  } = useFormContext();

  const { append, insert, remove, swap } = useFieldArray({
    name: "stops",
    control: control,
  });

  const { updateReadyTime } = useUpdateReadyTime();

  const { customFields } = useCustomFields({
    endCustomer: selectedCustomer,
  });

  useEffect(() => {
    setValue("_customFieldDefs", customFields);
  }, [customFields, setValue]);

  const dispatcherOrderEntryAdditionalFields = useMemo(() => {
    return (
      selectedCustomer?.dispatcherOrderEntryAdditionalFields ||
      courier?.courier?.dispatcherOrderEntryAdditionalFields ||
      []
    );
  }, [selectedCustomer, courier]);

  const watchStopsForCredentials = watch("stops");
  const watchPackageTypeId = watch("packageTypeId") as string | undefined | null;
  const watchPackageItems = watch("packageItems");
  const {
    requiredDocTypes,
    setRequiredDocTypes,
    warnedDocTypes,
    setWarnedDocTypes,
  } = useEditOrderDriverCredentials({
    courierId,
    orderId,
    selectedCustomerId: selectedCustomer?.id,
    watchStops: watchStopsForCredentials,
    shipmentSelectedForAction: shipment as unknown as Shipment | undefined,
    packageTypeId: watchPackageTypeId,
    packageItems: watchPackageItems,
  });

  // Sync credential state into form data so the submit handler can read it
  useEffect(() => {
    setValue("requiredDocumentTypes", requiredDocTypes);
    setValue("warnedDocumentTypes", warnedDocTypes);
  }, [requiredDocTypes, warnedDocTypes, setValue]);

  const {
    allPackageTypes,
    loading: arePackageTypesLoading,
    arePackageTypesResolved,
    error: packageTypesError,
  } = usePackageTypesForOrder(courier?.courier, selectedCustomer?.id);

  // Surfaced so the parent can block submit until the selected customer's list
  // has landed — a stale packageTypeId would otherwise be submittable.
  useEffect(() => {
    setArePackageTypesLoading(arePackageTypesLoading);
  }, [arePackageTypesLoading, setArePackageTypesLoading]);

  const { packageTypes } = useVisiblePackageTypes({
    allPackageTypes,
    vehicleTypes: GetVehicleTypesData?.vehicleTypes,
    arePackageTypesResolved,
    vehicleTypeFieldName: "vehicleTypeId",
    packageTypeFieldName: "packageTypeId",
    packageItemsFieldName: "packageItems",
  });
  const resolvedPackageTypeOrSizeMode = packageTypeOrSizeMode({
    hasBasePackage: true,
    packageTypes,
    arePackageTypesResolved,
    packageTypesError: packageTypesError
      ? PACKAGE_TYPES_LOAD_ERROR_MESSAGE
      : null,
  });

  useEffect(() => {
    if (getValues("packageTypeOrSizeMode") !== resolvedPackageTypeOrSizeMode) {
      setValue("packageTypeOrSizeMode", resolvedPackageTypeOrSizeMode);
    }
  }, [
    getValues,
    resolvedPackageTypeOrSizeMode,
    setValue,
  ]);

  const { hasMaxDimError } = usePackageDimensionsValidation({
    packageItemsFieldName: "packageItems",
    vehicleTypeIdFieldName: "vehicleTypeId",
    vehicleTypes: GetVehicleTypesData?.vehicleTypes,
  });

  useEffect(() => {
    setHasPackageValidationError(hasMaxDimError);
  }, [hasMaxDimError, setHasPackageValidationError]);

  const shipmentDate = watch("shipmentDate");
  const memoizedShipmentDate = useMemo(() => shipmentDate, [shipmentDate]);

  const {
    handleFixedDueTimeType,
    handleRelativeToPickupDueTimeType,
    handleFixedPickupCloseTimeType,
    handleRelativeToPickupCloseTimeType,
    showPickupCloseFields,
  } = useServiceTypeAutoFill({
    setSelectedDuration,
    memoizedShipmentDate,
  });

  useEffect(() => {
    const selectedEndCustomer = shipment?.order.endCustomer;
    if (selectedEndCustomer) {
      setSelectedCustomer(selectedEndCustomer as EndCustomer);
    }
  }, [shipment]);

  useEffect(() => {
    if (selectedCustomer) {
      setValue("endCustomerId", selectedCustomer.id);
      clearErrors("endCustomerId");
    } else {
      setValue("endCustomerId", undefined);
      setError("endCustomerId", {
        type: "manual",
        message: "Please select a customer for this order",
      });
    }
  }, [selectedCustomer]);

  const { onPackageItemRemoved } = useApplyPackageTypeDefaults({
    packageTypeId: watchPackageTypeId,
    packageTypes,
    arePackageTypesLoading,
    packageItemsFieldName: "packageItems",
    inheritShipmentPackageType: false,
    preserveInitialPackageValues: true,
  });

  const watchVehicleTypeId = watch("vehicleTypeId");
  const serviceType = watch("serviceType");

  const serviceVisibleVehicleTypes: VehicleType[] = useMemo(() => {
    const allVehicleTypes = GetVehicleTypesData?.vehicleTypes;
    if (!allVehicleTypes) {
      return [];
    }
    const visibleVehicleTypeIds = serviceType?.visibleVehicleTypeIds;
    if (!visibleVehicleTypeIds) {
      return allVehicleTypes;
    }
    return allVehicleTypes.filter((vehicleType) =>
      visibleVehicleTypeIds.includes(vehicleType.id),
    );
  }, [GetVehicleTypesData?.vehicleTypes, serviceType]);

  const { visibleVehicleTypes: vehicleTypes } =
    useFilterVehicleTypesByPackageTypes({
      vehicleTypes: GetVehicleTypesData?.vehicleTypes
        ? serviceVisibleVehicleTypes
        : undefined,
      vehicleTypeFieldName: "vehicleTypeId",
      packageTypeFieldName: "packageTypeId",
      packageItemsFieldName: "packageItems",
    });

  const watchStops = watch("stops");
  const { swapStops, canSwap } = useEditOrderSwapStops({ setValue, watch });
  const completedStopIds = useMemo(
    () => getCompletedStopIds(shipment?.stops ?? []),
    [shipment]
  );
  const stopOrder = useStopOrderControls({
    isEnabled: isStopOrderEditable,
    watchStops,
    completedStopIds,
    swap,
    insert,
    setValue,
    getValues,
    fieldArrayName: "stops",
    fieldArrayIndexNotation: "bracket",
  });
  const shipmentType = watch(`type`);
  const stopsLength = watch("stops")?.length;
  const { pickUpCount, dropOffCount } = watchStops
    ? watchStops.reduce(
        (acc, stop) => {
          if (stop.type === ShipmentStopType.PickUp) {
            acc.pickUpCount += 1;
          } else if (stop.type === ShipmentStopType.DropOff) {
            acc.dropOffCount += 1;
          }
          return acc;
        },
        { pickUpCount: 0, dropOffCount: 0 }
      )
    : { pickUpCount: 0, dropOffCount: 0 };

  useEffect(() => {
    if (courier?.courier) {
      getVehicleTypes({
        variables: {
          courierId: courier.courier.id,
        },
      });
    }
  }, [courier]);

  useEffect(() => {
    const allStopsHaveAddress =
      watchStops?.every((stop) => stop.streetAddress) ?? false;
    setIsAllStopsHaveAddress(allStopsHaveAddress);
  }, [JSON.stringify(watchStops), setIsAllStopsHaveAddress]);

  const changeStopsTypes = (shipmentType: string) => {
    const isMultiDropOff =
      shipmentType === ShipmentType.MultiDropOff ||
      shipmentType === ShipmentType.MultiDropOffRoundTrip;
    if (isMultiDropOff && watchStops.length > 1) {
      setValue(
        "stops",
        watchStops.map((stop, index) => ({
          ...stop,
          type:
            index === 0 ? ShipmentStopType.PickUp : ShipmentStopType.DropOff,
        }))
      );
    } else if (
      shipmentType === ShipmentType.MultiPickUp &&
      watchStops.length > 1
    ) {
      setValue(
        "stops",
        watchStops.map((stop, index) => ({
          ...stop,
          type:
            index === watchStops.length - 1
              ? ShipmentStopType.DropOff
              : ShipmentStopType.PickUp,
        }))
      );
    }
  };

  const handleChangeServiceType = (
    selectedServiceType: ServiceType | undefined
  ) => {
    if (selectedServiceType) {
      setValue("serviceType", selectedServiceType);
      if (selectedServiceType.dueTimeType === ServiceTypeDueTimeType.Fixed) {
        handleFixedDueTimeType(selectedServiceType);
      }
      if (
        selectedServiceType.deliveryTimeInMinutes &&
        selectedServiceType.dueTimeType ===
          ServiceTypeDueTimeType.RelativeToPickup
      ) {
        handleRelativeToPickupDueTimeType(selectedServiceType);
      }
      // Handle pickup close time auto-fill (when enabled)
      if (showPickupCloseFields && selectedServiceType.pickupCloseType) {
        if (
          selectedServiceType.pickupCloseType === ServiceTypeDueTimeType.Fixed
        ) {
          handleFixedPickupCloseTimeType(selectedServiceType);
        }
        if (
          selectedServiceType.pickupCloseType ===
          ServiceTypeDueTimeType.RelativeToPickup
        ) {
          handleRelativeToPickupCloseTimeType(selectedServiceType);
        }
      }
    } else {
      setValue("serviceType", undefined);
    }
  };

  function renderRemoveStopButton(stopType, index, stop: Stop) {
    if (dropOffCount === 1 && stopType === ShipmentStopType.DropOff) {
      return null;
    }
    if (pickUpCount === 1 && stopType === ShipmentStopType.PickUp) {
      return null;
    }
    if (stopsLength > 2) {
      return (
        <Button
          color={"secondary"}
          className="border truncate hover:bg-gray-100 dark:hover:bg-gray-800"
          size={"xs"}
          onClick={() => {
            if (stop.id) {
              setStopIdsToDelete([...stopIdsToDelete, stop.id]);
            }
            remove(index);
          }}
        >
          <XCircleIcon className="h-5 w-5 mr-2" />
          Remove Stop
        </Button>
      );
    }
  }

  const updateStopDates = () => {
    if (selectedDuration === "noStopDates") {
      return;
    }
    watchStops.forEach((stop, index) => {
      const stopDate = stop.stopDate;
      const stopType = stop.type;
      if (!stopDate) {
        if (stopType === ShipmentStopType.PickUp) {
          setValue(`stops.${index}.stopDate`, memoizedShipmentDate);
          const path = `stops.${index}.timeWindow.${"open"}`;
          const readyByTime = getValues(path);
          if (!readyByTime) {
            setValue(
              path,
              momentToTimePickerDate(moment(memoizedShipmentDate))
            );
          }
        } else {
          const nextDay = moment(memoizedShipmentDate).add(1, "days");
          setValue(`stops.${index}.stopDate`, momentToTimePickerDate(nextDay));
          setValue(
            `stops.${index}.deliverByDate`,
            momentToTimePickerDate(nextDay)
          );
          const path = `stops.${index}.timeWindow.${"close"}`;
          const deliverByTime = getValues(path);
          if (!deliverByTime) {
            setValue(
              path,
              momentToTimePickerDate(moment(nextDay).add(2, "hours"))
            );
          }
        }
      }
    });
  };

  const getVehicleTypeSelectValue = (value: string) => {
    if (!value || !GetVehicleTypesData?.vehicleTypes) return undefined;
    const selectedVehicleType = GetVehicleTypesData.vehicleTypes.find(
      (type) => type.id === value
    );
    if (!selectedVehicleType) return undefined;
    return [
      {
        id: selectedVehicleType?.id,
        label: selectedVehicleType?.name,
      },
    ];
  };

  return (
    <div className="grid grid-cols-3 gap-2 mb-20">
      <div className="col-span-2 flex justify-between gap-2">
        <div className="flex flex-col flex-1">
          <Label className="block text-xs font-medium text-gray-700 truncate">
            Customer{" "}
            {errors.endCustomerId && (
              <span className=" text-red-500">
                (Please select a customer for this order)
              </span>
            )}
          </Label>
          <EndCustomerSearchInput
            selectedCustomer={selectedCustomer}
            setSelectedCustomer={setSelectedCustomer}
            size="mini"
          />
        </div>
        <FormInput
          name="caller"
          label="Caller"
          control={control}
          errors={errors}
          required={isCallerRequired(dispatcherOrderEntryAdditionalFields)}
        />
        <div className="flex flex-col">
          <Label className="block text-xs font-medium text-gray-700 truncate">
            Caller Phone{" "}
            {isCallerPhoneRequired(dispatcherOrderEntryAdditionalFields) ? (
              <span className="text-orange-400 text-xs">(Required)</span>
            ) : (
              <span className="text-slate-400 text-xs">(Optional)</span>
            )}
          </Label>
          <CallerPhoneInput
            value={watch("callerPhone") || ""}
            selectedCustomer={selectedCustomer}
            error={!!errors.callerPhone}
            onSelect={(contact: CallerPhoneOption) => {
              setValue("callerPhone", contact.phone);
              if (contact.name) {
                setValue("caller", contact.name);
              }
            }}
            onChange={(value: string) => setValue("callerPhone", value)}
          />
        </div>
      </div>
      <CostCenterSelector
        control={control}
        setValue={setValue}
        order={shipment?.order as any}
      />
      <div className="col-span-3">
        <Controller
          control={control}
          name={"shipmentDate"}
          render={({ field }) => (
            <div>
              <Label className="block text-xs font-medium text-gray-700 truncate">
                Shipment Date
              </Label>
              <DatePicker
                size="mini"
                value={field.value}
                formatString={"MMM do, yyyy"}
                onChange={({ date }) => {
                  field.onChange(date);
                  updateReadyTime(date);
                }}
                autoFocusCalendar={false}
              />
            </div>
          )}
        />
      </div>
      <div className="flex col-span-3">
        <ol className="relative flex-1 ml-8 mt-1 text-gray-500 border-l border-primary-700 border-dashed dark:border-gray-700 dark:text-gray-400">
        {shipment &&
          watchStops &&
          watchStops.length > 0 &&
          watchStops.map((stop, index) => {
            const value = stop.streetAddress;
            const type = stop.type;
            const stableKey = `stop-${index}-${watchStops.length}`;
            return (
              <React.Fragment key={stableKey}>
                <StopForm
                  key={stableKey}
                  index={index}
                  errors={errors}
                  value={value}
                  selectedCustomer={selectedCustomer}
                  getValues={getValues}
                  clearErrors={clearErrors}
                  setError={setError}
                  setValue={setValue}
                  control={control}
                  selectedDuration={selectedDuration}
                  onStopDateChange={onStopDateChange}
                  selectedShipment={shipment as unknown as Shipment}
                  isSuiteRequired={isSuiteRequired(dispatcherOrderEntryAdditionalFields)}
                  isPhoneNumberRequired={isPhoneNumberRequired(
                    dispatcherOrderEntryAdditionalFields,
                  )}
                  isCompanyNameRequired={isCompanyNameRequired(
                    dispatcherOrderEntryAdditionalFields,
                  )}
                />
                <div className="ml-8 -mt-4 mb-8 flex flex-wrap gap-2">
                  <StopOrderRowControls stopOrder={stopOrder} index={index} />
                  {renderRemoveStopButton(type, index, stop)}
                </div>
              </React.Fragment>
            );
          })}
        </ol>
        {canSwap && (
          <div className="flex justify-center items-center pr-3">
            <Tooltip content="Swap Stops">
              <CourieButton color="gray" onClick={swapStops} type="button">
                <ArrowsUpDownIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
              </CourieButton>
            </Tooltip>
          </div>
        )}
      </div>
      <div className="col-span-3 px-4 mb-4 -mt-3">
        <AddStopButton
          append={append}
          insert={insert}
          shipmentType={shipmentType}
          watchStops={watchStops}
          onAddStop={() => updateStopDates()}
        />
      </div>
      <div className="col-span-3 grid grid-cols-5 gap-4">
        <div className="col-span-1">
          {shipment && (
            <>
              <Label className="block text-xs font-medium text-gray-700 truncate">
                Service Type
                {isServiceTypeRequired(dispatcherOrderEntryAdditionalFields) ? (
                  <span className="text-orange-400 text-xs"> (Required)</span>
                ) : (
                  <span className="text-slate-400 text-xs"> (Optional)</span>
                )}
              </Label>
              <ServiceTypeSelector
                control={control}
                errors={errors}
                size="mini"
                value={serviceType?.id}
                onChange={(value) => {
                  handleChangeServiceType(value as ServiceType | undefined);
                }}
                rules={
                  isServiceTypeRequired(dispatcherOrderEntryAdditionalFields)
                    ? { required: "Service Type is required" }
                    : {}
                }
                endCustomer={selectedCustomer}
              />
            </>
          )}
        </div>
        <div className="flex flex-col">
          <DurationTypeSelector
            setSelectedDuration={setSelectedDuration}
            selectedDuration={selectedDuration}
            onChange={() => updateStopDates()}
          />
        </div>
        <div className="col-span-1">
          <Label className="block text-xs font-medium text-gray-700">
            Stops
            <span className="text-red-500 ml-3">
              <FormErrorMessage errors={errors} name="type" />
            </span>
          </Label>
          <Controller
            name={`type`}
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange } }) => (
              <Select
                size="mini"
                disabled={isStopsDropdownDisabled(getValues("type"))}
                options={shipmentTypeArray}
                value={returnObjectForSelectValue(getValues)}
                placeholder=""
                searchable={false}
                clearable={false}
                error={errors?.shipments?.[0]?.type?.message}
                getValueLabel={({ option }) =>
                  typeof option.label === "string"
                    ? getOptionLabelForShipmentType(option.label)
                    : ""
                }
                getOptionLabel={({ option }) =>
                  typeof option.label === "string"
                    ? getOptionLabelForShipmentType(option.label)
                    : "test"
                }
                onChange={(params) => {
                  if (
                    params.value[0].label === ShipmentType.MultiDropOff ||
                    params.value[0].label ===
                      ShipmentType.MultiDropOffRoundTrip ||
                    params.value[0].label === ShipmentType.MultiPickUp
                  ) {
                    changeStopsTypes(params.value[0].label as string);
                  }
                  onChange(params.value[0].label);
                }}
              />
            )}
          />
        </div>
        <div className="col-span-1">
          <ControllerInput
            name={"endCustomerReferenceNumber"}
            label={"Customer Reference #"}
            className={"col-span-1"}
          />
        </div>
        {(vehicleTypes.length > 0 || serviceType?.visibleVehicleTypeIds) && (
          <div className="col-span-1 flex flex-col">
            <Label className="text-xs">
              Vehicle Type
              {isVehicleTypeRequired(dispatcherOrderEntryAdditionalFields) ? (
                <span className="text-orange-400 text-xs"> (Required)</span>
              ) : (
                <span className="text-slate-400 text-xs"> (Optional)</span>
              )}
            </Label>
            <Controller
              name={`vehicleTypeId`}
              control={control}
              rules={
                isVehicleTypeRequired(dispatcherOrderEntryAdditionalFields)
                  ? { required: "Vehicle Type is required" }
                  : {}
              }
              render={({ field: { onChange }, fieldState: { error } }) => {
                return (
                  <Select
                    size="mini"
                    backspaceRemoves={false}
                    clearable={true}
                    deleteRemoves={false}
                    escapeClearsValue={false}
                    error={error ? true : false}
                    options={vehicleTypes.map((type) => ({
                      id: type.id,
                      label: type.name,
                    }))}
                    value={getVehicleTypeSelectValue(
                      watchVehicleTypeId as string
                    )}
                    searchable={false}
                    onChange={(params) => {
                      const value =
                        params.value.length === 0
                          ? undefined
                          : params.value[0].id;
                      const selectedVehicleTypeId = vehicleTypes.find(
                        (pkg) => pkg.id === value
                      )?.id;
                      onChange(selectedVehicleTypeId);
                    }}
                  />
                );
              }}
            />
            <span className="text-red-500 text-xs">
              <FormErrorMessage errors={errors} name="vehicleTypeId" />
            </span>
          </div>
        )}
      </div>
      <PackageDimensionsForm
        fieldName="packageItems"
        onWeightChange={(total) => setValue("packageWeightInPounds", total)}
        className="col-span-3"
        packageTypes={packageTypes ?? []}
        onPackageItemRemoved={onPackageItemRemoved}
        hasBasePackage
        arePackageTypesResolved={arePackageTypesResolved}
        packageTypesError={
          packageTypesError ? PACKAGE_TYPES_LOAD_ERROR_MESSAGE : null
        }
        packageSizeFieldName="packageSize"
        shipmentPackageTypeIdFieldName="packageTypeId"
        numPackagesFieldName="numPackages"
        isTypeRequired={isPackageTypeRequired(dispatcherOrderEntryAdditionalFields)}
        isSizeRequired={isPackageSizeRequired(dispatcherOrderEntryAdditionalFields)}
        isQuantityRequired={isPackageQuantityRequired(
          dispatcherOrderEntryAdditionalFields,
        )}
        isWeightRequired={isPackageWeightRequired(
          dispatcherOrderEntryAdditionalFields,
        )}
        isDimensionsRequired={isPackageDimensionsRequired(
          dispatcherOrderEntryAdditionalFields,
        )}
      />
      <div className="col-span-3">
        <Label className="text-xs">
          Package Description
          {isPackageDescriptionRequired(dispatcherOrderEntryAdditionalFields) ? (
            <span className="text-orange-400 text-xs"> (Required)</span>
          ) : (
            <span className="text-slate-400 text-xs"> (Optional)</span>
          )}
        </Label>
        <Controller
          name={`packageDescription`}
          control={control}
          rules={
            isPackageDescriptionRequired(dispatcherOrderEntryAdditionalFields)
              ? { required: "Package Description is required" }
              : {}
          }
          render={({
            field: { onChange, value },
            fieldState: { error },
          }) => {
            return (
              <Textarea
                size="mini"
                placeholder="Enter Package Description"
                onChange={onChange}
                value={value}
                error={error ? true : false}
              />
            );
          }}
        />
        <span className="text-red-500 text-xs">
          <FormErrorMessage errors={errors} name="packageDescription" />
        </span>
      </div>
      <div className="col-span-3 grid grid-cols-12 gap-4">
        <OrderCredentialsInputs
          requiredDocTypes={requiredDocTypes}
          setRequiredDocTypes={setRequiredDocTypes}
          warnedDocTypes={warnedDocTypes}
          setWarnedDocTypes={setWarnedDocTypes}
        />
      </div>
      <div className="col-span-3">
        <Label className="text-xs">
          Additional Email Recipients{" "}
          <span className="text-slate-400 text-xs">(Optional)</span>
        </Label>
        <Controller
          control={control}
          name="additionalEmailRecipients"
          render={({ field }) => (
            <Input
              size="mini"
              value={field.value || ""}
              onChange={(event) => field.onChange(event.currentTarget.value)}
              onBlur={field.onBlur}
              type="text"
              placeholder="email1@example.com, email2@example.com"
            />
          )}
        />
      </div>
      {customFields?.map((field) => {
        if (field.type === CustomFieldType.Text) {
          return (
            <div key={field.id} className="flex flex-col">
              <FormInput
                name={`customFieldValues.${field.id}`}
                label={field.name}
                control={control}
                errors={errors}
                type="text"
                required={
                  field.requirementType ===
                  CustomFieldCustomerRequirementType.Required
                }
              />
            </div>
          );
        }
        if (field.type === CustomFieldType.Number || field.type === CustomFieldType.Integer) {
          const isInteger = field.type === CustomFieldType.Integer;
          const minValue = field.minValue ?? undefined;
          const maxValue = field.maxValue ?? undefined;
          const isTouched = !!(touchedFields as any)?.customFieldValues?.[field.id];
          const rangeLabel = getNumericFieldRangeLabel(minValue, maxValue);
          const hasRangeError = rangeLabel != null && isTouched && !!errors?.customFieldValues?.[field.id];
          return (
            <div key={field.id} className="flex flex-col">
              <FormInput
                name={`customFieldValues.${field.id}`}
                label={field.name}
                control={control}
                errors={errors}
                type="number"
                step={isInteger ? 1 : "any"}
                required={
                  field.requirementType ===
                  CustomFieldCustomerRequirementType.Required
                }
                min={minValue}
                max={maxValue}
              />
              {hasRangeError && (
                <span className="text-xs text-slate-400 dark:text-gray-500 mt-0.5">
                  {rangeLabel}
                </span>
              )}
            </div>
          );
        }
        if (field.type === CustomFieldType.MultipleChoice && field.choices) {
          const isRequired =
            field.requirementType ===
            CustomFieldCustomerRequirementType.Required;
          return (
            <div key={field.id} className="flex flex-col">
              <Label className="text-xs">
                {field.name}{" "}
                {isRequired ? (
                  <span className="text-orange-400 text-xs">(Required)</span>
                ) : (
                  <span className="text-slate-400 text-xs">(Optional)</span>
                )}
              </Label>
              <Controller
                name={`customFieldValues.${field.id}`}
                control={control}
                rules={isRequired ? { required: `${field.name} is required` } : {}}
                render={({ field: controllerField, fieldState: { error } }) => (
                  <Select
                    size="mini"
                    clearable={!isRequired}
                    searchable={false}
                    error={error ? true : false}
                    options={field.choices!.map((choice) => ({
                      id: choice,
                      label: choice,
                    }))}
                    value={
                      controllerField.value
                        ? [{ id: controllerField.value, label: controllerField.value }]
                        : []
                    }
                    onChange={(params) => {
                      const value =
                        params.value.length === 0
                          ? undefined
                          : params.value[0].id;
                      controllerField.onChange(value);
                    }}
                  />
                )}
              />
              {errors?.customFieldValues?.[field.id] && (
                <span className="text-red-500 text-xs">
                  {errors.customFieldValues[field.id]?.message}
                </span>
              )}
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

export default EditOrderForm;
